import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Check, X, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import RupiahInput from "@/components/RupiahInput";
import DateField from "@/components/DateField";
import ExpenseWeeklyChart from "@/components/ExpenseWeeklyChart";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { computeWeeksOfYear, fmtShortDate } from "@/components/petty-cash/utils";

const toDateInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return ""; }
};

/**
 * Expense ledger dialog — BOD & FAT. Now records ONE weekly TOTAL per submission
 * (bukan per-item). Pengguna memilih Tahun + Minggu (Sen–Min) sama seperti Weekly
 * Petty Cash; sistem otomatis isi tanggal end-of-week + label note "Pengeluaran
 * minggu {label} ({start}–{end})".
 */
export default function ExpenseLedgerDialog({ project, open, onOpenChange, onChanged, readOnly = false }) {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {id, date, amount, note}
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null });
  // Combined mode — mirror IncomeLedgerDialog. When parent has vo_aggregation="combined",
  // merge expense_entries from parent + all children into single view (read-only for
  // entries from other projects — user must edit at source).
  const [combined, setCombined] = useState(null);
  const isCombinedParent = project?.project_type === "new_project" && project?.vo_aggregation === "combined";

  // Weekly submission state (replaces free-text date + note for new entries)
  const [weekYear, setWeekYear] = useState(new Date().getFullYear());
  const [weekNo, setWeekNo] = useState(null);
  const [amount, setAmount] = useState("");
  const [extraNote, setExtraNote] = useState("");

  const weeks = useMemo(() => computeWeeksOfYear(weekYear), [weekYear]);
  const selectedWeek = useMemo(() => weeks.find((w) => w.no === weekNo) || null, [weeks, weekNo]);

  // Detect duplicate week entry — one weekly total per week (edit existing instead).
  const duplicateEntry = useMemo(() => {
    if (!selectedWeek) return null;
    const marker = `Pengeluaran minggu ${selectedWeek.label}`;
    return entries.find((e) => (e.note || "").startsWith(marker)) || null;
  }, [entries, selectedWeek]);

  useEffect(() => {
    if (open && project) {
      setEntries(project.expense_entries || []);
      const now = new Date();
      const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const yearNow = now.getFullYear();
      setWeekYear(yearNow);
      const ws = computeWeeksOfYear(yearNow);
      const current = ws.find((w) => todayISO >= w.start && todayISO <= w.end);
      setWeekNo(current ? current.no : (ws[0]?.no ?? 1));
      setAmount("");
      setExtraNote("");
      // Load combined summary — same pattern as IncomeLedgerDialog
      if (project.project_type !== "internal") {
        api.get(`/active-projects/${project.id}/combined-summary`)
          .then(({ data }) => {
            setCombined(data && data.is_combined ? data : null);
            if (data && data.is_combined) setEntries(data.expense_entries || []);
          })
          .catch(() => setCombined(null));
      } else {
        setCombined(null);
      }
    }
  }, [open, project]);

  const total = useMemo(() => entries.reduce((s, e) => s + (e.amount || 0), 0), [entries]);

  const add = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
    if (!selectedWeek) { toast.error("Pilih minggu dulu"); return; }
    if (duplicateEntry) {
      toast.error(`Minggu ${selectedWeek.label} sudah ada entri (${formatIDR(duplicateEntry.amount)}). Silakan edit entri yang ada di tabel bawah.`);
      // Auto-scroll to and highlight editing the existing entry
      startEdit(duplicateEntry);
      return;
    }
    const baseNote = `Pengeluaran minggu ${selectedWeek.label} (${fmtShortDate(selectedWeek.start)}–${fmtShortDate(selectedWeek.end)})`;
    const finalNote = extraNote.trim() ? `${baseNote} · ${extraNote.trim()}` : baseNote;
    setBusy(true);
    try {
      const { data } = await api.post(`/active-projects/${project.id}/expense`, {
        date: new Date(`${selectedWeek.end}T23:59:59`).toISOString(),
        amount: amt,
        note: finalNote,
        // 2026-09-23 (user request): auto-tag agar entri Buku Pengeluaran Mingguan
        // langsung terklasifikasi "Petty Cash" di tab Pengeluaran — tidak lagi
        // bergantung pola teks note (isPettyCarryEntry cek finance_source dulu).
        finance_source: "petty_cash_carry",
      });
      setEntries((es) => [...es, data]);
      setAmount("");
      setExtraNote("");
      toast.success(`Expense minggu ${selectedWeek.label} tersimpan`);
      onChanged?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (eid) => {
    try {
      await api.delete(`/active-projects/${project.id}/expense/${eid}`);
      setEntries((es) => es.filter((e) => e.id !== eid));
      toast.success("Expense deleted");
      onChanged?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };
  const askDel = (eid) => setConfirmDel({ open: true, id: eid });

  const startEdit = (e) => {
    setEditing({ id: e.id, date: toDateInput(e.date), amount: String(e.amount ?? ""), note: e.note || "" });
  };
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    const amt = parseFloat(editing.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
    try {
      const { data } = await api.patch(`/active-projects/${project.id}/expense/${editing.id}`, {
        date: new Date(editing.date).toISOString(),
        amount: amt,
        note: editing.note,
      });
      setEntries((es) => es.map((e) => (e.id === editing.id ? data : e)));
      setEditing(null);
      toast.success("Expense updated");
      onChanged?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dialog-expense">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Expense Ledger · {project?.project_name}</DialogTitle>
          <DialogDescription>Catat total pengeluaran per <span className="font-semibold">minggu</span> (bukan per item). Sistem otomatis mengisi tanggal end-of-week dan label minggu — sama seperti Weekly Petty Cash.</DialogDescription>
        </DialogHeader>

        <div className="bg-rose-50 border border-rose-200 p-3 flex items-center justify-between">
          <span className="overline text-rose-800">Total Expense</span>
          <span data-testid="expense-total" className="font-display text-xl tabular-nums text-rose-800">{formatIDR(total)}</span>
        </div>

        {entries.length > 0 && <ExpenseWeeklyChart entries={entries} />}

        <form onSubmit={add} className={`border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-12 gap-3 ${readOnly ? "hidden" : ""}`}>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="overline text-slate-600 inline-flex items-center gap-1"><Calendar size={11} /> Tahun</Label>
            <select
              data-testid="expense-year"
              value={weekYear}
              onChange={(e) => { setWeekYear(Number(e.target.value)); setWeekNo(1); }}
              className="rounded-none h-10 w-full border border-slate-300 bg-white px-2 text-sm"
            >
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4 space-y-1.5">
            <Label className="overline text-slate-600">Minggu (Sen–Min)</Label>
            <select
              data-testid="expense-week"
              required
              value={weekNo || ""}
              onChange={(e) => setWeekNo(Number(e.target.value))}
              className="rounded-none h-10 w-full border border-slate-300 bg-white px-2 text-sm"
            >
              <option value="">Pilih minggu…</option>
              {weeks.map((w) => (
                <option key={w.no} value={w.no}>{w.label} · {fmtShortDate(w.start)} – {fmtShortDate(w.end)}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="overline text-slate-600">Total (IDR)</Label>
            <RupiahInput testid="expense-amount" value={amount} onChange={(v) => setAmount(v)} />
          </div>
          <div className="md:col-span-4 space-y-1.5">
            <Label className="overline text-slate-600">Catatan (opsional)</Label>
            <Input data-testid="expense-note" value={extraNote} onChange={(e) => setExtraNote(e.target.value)} className="rounded-none" placeholder="Contoh: mobilisasi lapangan" />
          </div>
          <div className="md:col-span-12 flex items-center justify-end">
            <Button type="submit" data-testid="expense-submit" disabled={busy || !selectedWeek || !!duplicateEntry} className="rounded-none bg-rose-600 hover:bg-rose-700 uppercase text-xs tracking-wider px-6 disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? "…" : "Add"}
            </Button>
          </div>
          {duplicateEntry && (
            <div className="md:col-span-12 -mt-1 px-3 py-2 border border-amber-300 bg-amber-50 text-[11px] text-amber-900 flex items-center justify-between gap-2" data-testid="expense-week-duplicate">
              <span>
                Minggu <span className="font-semibold">{selectedWeek?.label}</span> sudah ada entri <span className="font-semibold tabular-nums">{formatIDR(duplicateEntry.amount)}</span>. Silakan edit entri tersebut daripada menambah baru.
              </span>
              <button
                type="button"
                data-testid="expense-week-duplicate-edit"
                onClick={() => startEdit(duplicateEntry)}
                className="px-2 py-1 bg-amber-700 hover:bg-amber-800 text-white uppercase tracking-wider text-[10px] font-semibold"
              >
                Edit Existing
              </button>
            </div>
          )}
          {selectedWeek && !duplicateEntry && (
            <div className="md:col-span-12 text-[10px] text-slate-500 -mt-2" data-testid="expense-week-preview">
              Akan dicatat sebagai: <span className="font-mono text-slate-700">Pengeluaran minggu {selectedWeek.label} ({fmtShortDate(selectedWeek.start)}–{fmtShortDate(selectedWeek.end)}){extraNote.trim() ? ` · ${extraNote.trim()}` : ""}</span> · tanggal end-of-week {fmtShortDate(selectedWeek.end)}
            </div>
          )}
        </form>

        <div className="border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">{["Date", "Amount", "Note", "Recorded By", ""].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No expense entries yet.</td></tr>}
              {entries.map((e) => {
                const isEd = editing?.id === e.id;
                if (isEd) {
                  return (
                    <tr key={e.id} className="border-b border-slate-100 bg-rose-50/40">
                      <td className="px-3 py-2 min-w-[140px]">
                        <DateField testid={`expense-edit-date-${e.id}`} value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
                      </td>
                      <td className="px-3 py-2">
                        <RupiahInput testid={`expense-edit-amount-${e.id}`} value={editing.amount} onChange={(v) => setEditing({ ...editing, amount: v })} className="h-7 text-xs" />
                      </td>
                      <td className="px-3 py-2">
                        <Input data-testid={`expense-edit-note-${e.id}`} value={editing.note} onChange={(ev) => setEditing({ ...editing, note: ev.target.value })} className="rounded-none h-7 text-xs" />
                      </td>
                      <td className="px-3 py-2 text-slate-500">{e.recorded_by_name}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button data-testid={`expense-save-${e.id}`} onClick={saveEdit} className="text-emerald-600 hover:text-emerald-800 p-1"><Check size={13} /></button>
                          <button data-testid={`expense-cancel-${e.id}`} onClick={cancelEdit} className="text-slate-500 hover:text-slate-700 p-1"><X size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={e.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 tabular-nums">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold text-rose-700">{formatIDR(e.amount)}</td>
                    <td className="px-3 py-2 text-slate-600">{e.note || "-"}</td>
                    <td className="px-3 py-2 text-slate-500">{e.recorded_by_name}</td>
                    <td className="px-3 py-2 text-right">
                      {readOnly ? (
                        <span className="text-[10px] text-slate-400 italic">view only</span>
                      ) : (
                        <div className="inline-flex gap-1">
                          <button data-testid={`expense-edit-${e.id}`} onClick={() => startEdit(e)} className="text-slate-500 hover:text-[#0B1D3A] p-1" title="Edit"><Pencil size={12} /></button>
                          <button data-testid={`expense-del-${e.id}`} onClick={() => askDel(e.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete"><Trash2 size={13} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel({ ...confirmDel, open: o })}
        title="Delete this expense entry?"
        description="The amount will be removed from Total Expense for this project."
        confirmLabel="Delete"
        onConfirm={() => {
          const id = confirmDel.id;
          setConfirmDel({ open: false, id: null });
          if (id) del(id);
        }}
        testid="confirm-del-expense"
      />
    </Dialog>
  );
}
